import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle, Plus, Trash2, Layers } from "lucide-react";
import { LayerSelectorModal } from "./LayerSelectorModal";
import { motion, useDragControls, useMotionValue } from "motion/react";

interface ViewsConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (viewsConfigList: ViewConfigItem[]) => void;
  planType: string;
  initialViews?: ViewConfigItem[];
}

export interface ViewConfigItem {
  id: string;
  name: string;
  offLayers: string[];
}

export const ViewsConfigModal: React.FC<ViewsConfigModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  planType,
  initialViews
}) => {
  const defaultViews = [
    { id: "1", name: "Standard Plan", offLayers: [] },
    { id: "2", name: "Sales Productivity PSF", offLayers: [] },
    { id: "3", name: "Occupancy Cost", offLayers: [] },
    { id: "4", name: "Total Rent PSF", offLayers: [] },
    { id: "5", name: "Base Plan", offLayers: [] },
  ];

  const [views, setViews] = useState<ViewConfigItem[]>(initialViews || defaultViews);

  // Sync initialViews when modal opens
  useEffect(() => {
    if (isOpen) {
      setViews(initialViews && initialViews.length > 0 ? initialViews : defaultViews);
    }
  }, [isOpen, initialViews]);

  const [activeLayerSelectViewId, setActiveLayerSelectViewId] = useState<string | null>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Reset position on open
  useEffect(() => {
    if (isOpen) {
      x.set(0);
      y.set(0);
    }
  }, [isOpen, x, y]);

  if (!isOpen) return null;

  const handleAddView = () => {
    const newId = Date.now().toString();
    setViews([...views, { id: newId, name: `New View ${views.length + 1}`, offLayers: [] }]);
  };

  const handleRemoveView = (id: string) => {
    setViews(views.filter(v => v.id !== id));
  };

  const handleNameChange = (id: string, newName: string) => {
    setViews(views.map(v => v.id === id ? { ...v, name: newName } : v));
  };

  const handleSaveLayers = (offLayers: string[]) => {
    if (activeLayerSelectViewId) {
      setViews(views.map(v => 
        v.id === activeLayerSelectViewId 
          ? { ...v, offLayers } 
          : v
      ));
    }
    setActiveLayerSelectViewId(null);
  };

  const handleGenerate = () => {
    if (views.length === 0) {
      alert("Please add at least one view.");
      return;
    }
    onGenerate(views);
  };

  const activeView = views.find(v => v.id === activeLayerSelectViewId);

  return createPortal(
    <div className="fixed inset-0 z-[1000000005] flex items-center justify-center pointer-events-none">
      <motion.div 
        drag
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden pointer-events-auto"
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4 bg-primary-700 cursor-move"
          onPointerDown={(e) => dragControls.start(e)}
        >
          <div>
            <h2 className="text-lg font-semibold text-white pointer-events-none">Configure views.json</h2>
            <p className="text-xs text-primary-200 pointer-events-none">Plan Type: {planType}</p>
          </div>
          <button 
            onClick={onClose} 
            className="text-primary-200 hover:text-white"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50">
          {views.map((view, index) => (
            <div key={view.id} className="flex items-center gap-2 bg-white p-2 border rounded-md shadow-sm">
              <div className="flex-none text-gray-400 font-medium w-5 text-center text-sm">
                {index + 1}.
              </div>
              <input
                type="text"
                value={view.name}
                onChange={(e) => handleNameChange(view.id, e.target.value)}
                className="flex-1 rounded-md border-0 py-1.5 px-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 text-sm"
                placeholder="Information Layer Name"
              />
              <button
                type="button"
                onClick={() => setActiveLayerSelectViewId(view.id)}
                className="inline-flex items-center gap-x-1 rounded-md bg-white px-2 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                <Layers className="-ml-0.5 h-3.5 w-3.5 text-blue-500" />
                Set Layers
                {view.offLayers.length > 0 && (
                  <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                    {view.offLayers.length} off
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => handleRemoveView(view.id)}
                className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAddView}
            className="w-full mt-2 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-gray-300 p-3 text-sm font-semibold text-gray-600 hover:border-gray-400 hover:bg-gray-50"
          >
            <Plus className="h-4 w-4" />
            Add new Information Layer
          </button>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Generate views.json
          </button>
        </div>
      </motion.div>

      {activeLayerSelectViewId && activeView && (
        <LayerSelectorModal
          isOpen={true}
          onClose={() => setActiveLayerSelectViewId(null)}
          onSave={handleSaveLayers}
          initialOffLayers={activeView.offLayers}
          viewName={activeView.name}
        />
      )}
    </div>,
    document.body
  );
};
